import type { MouseEvent, ReactNode } from 'react'
import { unlockAmounts, useBlurAmounts } from '../../lib/blur-amounts'
import { cn } from '../../lib/utils'

interface PrivateAmountProps {
  children: ReactNode
  className?: string
  /**
   * 'none' waar hover geen echte interactie is: in een tooltip die zelf al het
   * resultaat van een hover is, of op een bedrag dat binnen een knop ligt. Zo'n
   * bedrag volgt alleen de globale toestand.
   */
  reveal?: 'hover' | 'none'
}

/**
 * Een geldbedrag dat vervaagt zolang de voorkeur aan staat en niemand ontgrendeld
 * heeft. Hover leest een enkel bedrag (via CSS), klikken zet het hele dashboard
 * scherp.
 *
 * Het is altijd een span en nooit conditioneel een ander element: zou het type
 * wisselen, dan unmount React het kind en begint de count-up van AnimatedValue
 * opnieuw bij nul zodra je ontgrendelt.
 *
 * Bewust geen aria-label en geen aria-hidden. De blur beschermt tegen iemand die
 * over je schouder meekijkt, niet tegen de schermlezer van de gebruiker zelf, en
 * een label zou het bedrag uit de accessible name duwen. De toetsenbord-ingang is
 * de knop in de paginakop.
 */
export function PrivateAmount({ children, className, reveal = 'hover' }: PrivateAmountProps) {
  const { blurred } = useBlurAmounts()

  if (!blurred) return <span className={className}>{children}</span>

  const onClick = (event: MouseEvent<HTMLSpanElement>) => {
    // Bedragen staan soms in een Link of een knop; ontgrendelen mag daar niet ook
    // navigeren of een lijst uitklappen.
    event.preventDefault()
    event.stopPropagation()
    unlockAmounts()
  }

  return (
    <span
      className={cn(className, 'amount-shield', reveal === 'hover' && 'cursor-pointer')}
      data-blurred="true"
      onClick={reveal === 'hover' ? onClick : undefined}
    >
      {children}
    </span>
  )
}
